// Shared time/date helpers for admin event display

// Convert 24-hour time to 12-hour AM/PM format
export const formatTime12h = (time24) => {
  if (!time24) return '—';
  try {
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch {
    return time24;
  }
};

// Convert 12-hour time (e.g., "2:00 PM", "10:30 AM") to 24-hour format
export const convertTo24Hour = (time12h) => {
  if (!time12h) return null;
  try {
    // Match patterns like "2:00 PM", "10:30 AM", "2 PM", "10AM"
    const match = time12h.match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM|am|pm)/i);
    if (!match) return null;

    let hours = parseInt(match[1], 10);
    const minutes = match[2] ? parseInt(match[2], 10) : 0;
    const period = match[3].toUpperCase();

    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  } catch {
    return null;
  }
};

// Extract start time from notes (looks for "Program start time:" or "Event Time On Web:")
export const extractStartTimeFromNotes = (notes) => {
  if (!notes) return null;

  // Pattern 1: "Program start time: X:XX" or "Program start time: X:XX PM"
  const programMatch = notes.match(/Program start time[:\s]+(\d{1,2}(?::\d{2})?\s*(?:AM|PM)?)/i);
  if (programMatch) {
    const timeStr = programMatch[1];
    // If it already has AM/PM, convert it
    if (/AM|PM/i.test(timeStr)) {
      return convertTo24Hour(timeStr);
    }
    // If no AM/PM, assume it's already in a usable format
    return timeStr.includes(':') ? timeStr : `${timeStr}:00`;
  }

  // Pattern 2: "Event Time On Web: X - Y PM" or "Event Time On Web: X:XX - Y:XX PM"
  const webMatch = notes.match(/Event Time On Web[:\s]+(\d{1,2}(?::\d{2})?)\s*(?:-|to)\s*(\d{1,2}(?::\d{2})?)\s*(AM|PM)/i);
  if (webMatch) {
    const startHour = webMatch[1];
    const period = webMatch[3];
    const startTimeStr = startHour.includes(':') ? `${startHour} ${period}` : `${startHour}:00 ${period}`;
    return convertTo24Hour(startTimeStr);
  }

  // Pattern 3: Simple "Event Time On Web: X PM" or "Event Time On Web: X:XX PM"
  const simpleWebMatch = notes.match(/Event Time On Web[:\s]+(\d{1,2}(?::\d{2})?\s*(?:AM|PM))/i);
  if (simpleWebMatch) {
    return convertTo24Hour(simpleWebMatch[1]);
  }

  return null;
};

// Get the effective start time (from notes if available, otherwise from event data)
export const getEffectiveStartTime = (startTime, notes) => {
  const notesStartTime = extractStartTimeFromNotes(notes);
  return notesStartTime || startTime;
};

// Calculate crew call time (1 hour before start time)
export const calculateCallTime = (startTime, existingCallTime, notes) => {
  // First check if there's a start time in the notes
  const notesStartTime = extractStartTimeFromNotes(notes);
  const effectiveStartTime = notesStartTime || startTime;

  // If call_time is already set and different from start_time, use it (unless we found a notes time)
  if (!notesStartTime && existingCallTime && existingCallTime !== startTime) {
    return existingCallTime;
  }

  // Calculate 1 hour before the effective start time
  if (!effectiveStartTime) return null;
  try {
    const [hours, minutes] = effectiveStartTime.split(':').map(Number);
    const callHours = hours - 1;
    if (callHours < 0) return effectiveStartTime; // Edge case: if start time is before 1am
    return `${callHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  } catch {
    return effectiveStartTime;
  }
};

// Get today's date string in YYYY-MM-DD format
export const getTodayString = () => {
  return new Date().toISOString().split('T')[0];
};
